import { trackEvent } from '@patrianna/analytics'
import { Checkbox, FormControlLabel, Form, FormTextField } from '@patrianna/core-components'
import { useMediaQuery } from '@patrianna/shared-hooks'
import { getAuthFlowLoadingSelector } from '@patrianna/shared-store/auth'
import { nextTick } from '@patrianna/shared-utils'
import { passwordValidator } from '@patrianna/shared-utils/hookFormValidators/passwordValidator'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import cx from 'classnames'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import Button from 'components/Button'
import ReferredByField from 'components/ReferredByField'
import TermsCheckbox from 'components/TermsCheckbox'
import { useRouterPushWithCallback } from 'containers/NavigationProvider'
import useAffiliateFormValuesExtension from 'hooks/useAffiliateFormValuesExtension'
import useSaveRegistrationData from 'hooks/useSaveRegistrationData'
import authPages from 'prismic-translates/auth-pages.json'
import formValidatorsContent from 'prismic-translates/form-validators.json'
import loginPage from 'prismic-translates/loginpage.json'
import { FormPasswordField } from 'src/components/Form/FormPasswordField'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { emailValidatorHookForm, nameValidator } from 'src/utils/formValidators'
import { getPasswordPolicySelector } from 'store/modules/appConfig/selectors'
import { registerViaEmail } from 'store/modules/auth/actions'
import type { RegisterViaEmail } from 'store/modules/auth/types'

import classes from './styles.module.scss'

const t = getTranslationFromJson([authPages, formValidatorsContent, loginPage])

type RegisterFormTouchedType = Record<keyof RegisterViaEmail, boolean>

const defaultInitialValues: RegisterViaEmail = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  referredBy: '',
}

const defaultTouched: RegisterFormTouchedType = {
  firstName: false,
  lastName: false,
  email: false,
  password: false,
  referredBy: false,
}

function RegisterForm() {
  const routerPush = useRouterPushWithCallback()
  const query = useSearchParams()
  const pathname = usePathname()
  const [terms, agreeTerms] = useState(false)
  const [IsUserInteractWithForm, setUserInteractWithForm] = useState(null)
  const [isTermsChecked, setIsTermsChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isMobile = useMediaQuery('xs')
  const dispatch = useAppDispatch()
  const isAuthLoading = useAppSelector(getAuthFlowLoadingSelector)
  const passwordPolicy = useAppSelector(getPasswordPolicySelector)

  const defaultValues = {
    ...defaultInitialValues,
  }

  const [initialRegistrationValues, , saveInitialValues, removeSavedValues] = useSaveRegistrationData<
    RegisterViaEmail,
    RegisterFormTouchedType
  >('MANUAL_REG_VALUES', defaultValues, defaultTouched)

  const [initialValues, transformAffiliate] = useAffiliateFormValuesExtension(initialRegistrationValues)
  const methods = useForm<RegisterViaEmail>({ mode: 'onTouched', defaultValues })

  const {
    formState: { isValid },
    reset,
    getValues,
    watch,
  } = methods

  const emailValue = watch('email')

  const onSubmit = (values: RegisterViaEmail) => {
    setIsSubmitting(true)
    nextTick().then(() => {
      const transformedValues = { ...values }
      transformedValues.referredBy = transformAffiliate(values.referredBy)

      removeSavedValues()
      dispatch(registerViaEmail(transformedValues, setIsSubmitting, isMobile, routerPush, query, pathname))
    })
  }

  const onClick = () => {
    trackEvent('register_manual_page_register_button')
  }

  useEffect(() => {
    trackEvent('sign_up_step_displayed', {
      interaction_name: 'sign_up_step_displayed',
      sign_up_total_steps: 1,
      sign_up_current_step_number: 1,
    })
  }, [])

  const userInteractWithForm = (name: string) => {
    if (!IsUserInteractWithForm) {
      setUserInteractWithForm(true)
      trackEvent('sign_up_started', {
        interaction_name: 'sign_up_started',
        sign_up_total_steps: 1,
        sign_up_current_step_number: 1,
        sign_up_interacted_element: name,
        sign_up_method: 'manual',
      })
    }
  }

  const termsCheckboxHandler = () => {
    agreeTerms(!terms)

    if (!isTermsChecked) {
      setIsTermsChecked(true)

      trackEvent('sign_up_terms_checked', {
        step_name: 'Terms Of Service',
        sign_up_total_steps: 1,
        interaction_name: 'sign_up_terms_checked',
        sign_up_current_step_number: 1,
        sign_up_method: 'manual',
      })
    }
  }

  return (
    <>
      <Form methods={methods} onSubmit={onSubmit} className={classes.root}>
        <FormTextField
          dataTestId='first-name-input'
          name='firstName'
          className={cx(classes.half, classes.input)}
          placeholder={t('first_name')}
          maxLength={29}
          rules={nameValidator(t('invalid_name'))}
        />
        <FormTextField
          dataTestId='last-name-input'
          name='lastName'
          className={cx(classes.half, classes.input)}
          placeholder={t('second_name')}
          maxLength={39}
          rules={nameValidator(t('invalid_name'))}
        />
        <FormTextField
          dataTestId='email-input'
          name='email'
          className={classes.input}
          onFocus={() => userInteractWithForm('email_address')}
          placeholder={t('email_address')}
          rules={emailValidatorHookForm(t('enter_valid_email_address'), t('enter_email_address'))}
        />

        <FormPasswordField
          dataTestId='password-input'
          name='password'
          className={classes.input}
          rules={{
            ...passwordValidator(passwordPolicy),
            validate: {
              ...passwordValidator(passwordPolicy).validate,
              shouldNotMatchEmail: (value: string) => {
                return emailValue.toLowerCase() === value.toLowerCase() ? t('choose_a_different_password') : true
              },
            },
          }}
          placeholder={t('password')}
        />
        <ReferredByField inputClassName={classes.input} initialChecked={!!initialValues.referredBy} />
        <FormControlLabel
          className={cx(classes.agreeField, classes.checkbox)}
          labelClassName={classes.label}
          control={
            <Checkbox dataTestId='terms-checkbox' checked={terms} onChange={termsCheckboxHandler} value={terms} />
          }
          label={
            <TermsCheckbox
              onClick={() => {
                saveInitialValues(getValues())
                reset(defaultValues)
              }}
            />
          }
        />
        <Button
          dataTestId='play-now-button'
          type='submit'
          className={classes.button}
          disabled={!terms || !isValid || isSubmitting || isAuthLoading}
          onClick={onClick}
        >
          {t('register_form_button')}
        </Button>
      </Form>
    </>
  )
}

export default RegisterForm
